/*
    TeamLineups Component
    This component is used to display the team lineups data for a given game.

    For NFL, it will display:
        - Depth Charts, Grouped by Position Category (Off, Def) and Position
        - Sections for Off and Def
        - Table for each position category (QB, RB, WR, TE) with Player Stats
            - QB Stats - PassingYardsPerGame, PassingTouchdownsPerGame, PassingRating
            - RB Stats - RushingYardsPerGame, RushingYardsPerAttempt, RushingTouchdownsPerGame
            - WR / TE Stats - ReceivingYardsPerGame, ReceivingYardsPerReception, ReceivingTouchdownsPerGame
    

    This component uses a unified comparative layout where both teams' stats are displayed together 
    in each section for easier comparison, with clear team identification. When on Mobile there should be a switch to toggle between the two teams..

    
    Props: 
        - game: the game object
        - sport: sport identifier (e.g., 'NFL')
        - teamDepthCharts: array of team depth chart objects with TeamID, Offense, Defense, SpecialTeams arrays
        - teamStats: object keyed by TeamID containing arrays of player stat objects
        - homeTeam: home team object
        - awayTeam: away team object
*/

import React, { useState } from 'react';

// Position groups for NFL
const OFFENSIVE_POSITIONS = ['QB', 'RB', 'FB', 'LWR', 'RWR', 'SWR', 'TE', 'LT', 'LG', 'C', 'RG', 'RT'];
const DEFENSIVE_POSITIONS = ['LDE', 'RDE', 'NT', 'LOLB', 'LILB', 'RILB', 'ROLB', 'LCB', 'RCB', 'NB', 'FS', 'SS'];

// Helper function to get status badge styling (matching InjuriesView)
const getStatusStyling = (status) => {
    const statusLower = status?.toLowerCase() || '';
    
    if (statusLower.includes('out') || statusLower.includes('inactive')) {
        return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-300 dark:border-red-700';
    }
    if (statusLower.includes('doubtful')) {
        return 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 border-orange-300 dark:border-orange-700';
    }
    if (statusLower.includes('questionable') || statusLower.includes('day-to-day')) {
        return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-yellow-300 dark:border-yellow-700';
    }
    if (statusLower.includes('probable')) {
        return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-300 dark:border-green-700';
    }
    
    // Default
    return 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300 border-gray-300 dark:border-gray-600';
};

// Stat configurations for different positions
const POSITION_STATS = {
    QB: [
        { key: 'PassingYardsPerGame', label: 'Pass Yds/G', format: (val) => val?.toFixed(1) || 'N/A' },
        { key: 'PassingTouchdownsPerGame', label: 'Pass TD/G', format: (val) => val?.toFixed(1) || 'N/A' },
        { key: 'PassingRating', label: 'Rating', format: (val) => val?.toFixed(1) || 'N/A' }
    ],
    RB: [
        { key: 'RushingYardsPerGame', label: 'Rush Yds/G', format: (val) => val?.toFixed(1) || 'N/A' },
        { key: 'RushingYardsPerAttempt', label: 'Yds/Att', format: (val) => val?.toFixed(1) || 'N/A' },
        { key: 'RushingTdsPerGame', label: 'Rush TD/G', format: (val) => val?.toFixed(1) || 'N/A' }
    ],
    WR: [
        { key: 'ReceivingYardsPerGame', label: 'Rec Yds/G', format: (val) => val?.toFixed(1) || 'N/A' },
        { key: 'ReceivingYardsPerReception', label: 'Yds/Rec', format: (val) => val?.toFixed(1) || 'N/A' },
        { key: 'ReceivingTouchdownsPerGame', label: 'Rec TD/G', format: (val) => val?.toFixed(1) || 'N/A' }
    ],
    TE: [
        { key: 'ReceivingYardsPerGame', label: 'Rec Yds/G', format: (val) => val?.toFixed(1) || 'N/A' },
        { key: 'ReceivingYardsPerReception', label: 'Yds/Rec', format: (val) => val?.toFixed(1) || 'N/A' },
        { key: 'ReceivingTouchdownsPerGame', label: 'Rec TD/G', format: (val) => val?.toFixed(1) || 'N/A' }
    ]
};

// Positions that should display stats
const STAT_POSITIONS = ['QB', 'RB', 'LWR', 'RWR', 'SWR', 'TE'];

const NflTeamLineups = ({ game, teamDepthCharts, teamStats, homeTeamID, awayTeamID, homeTeamLabel = null, awayTeamLabel = null, homeTeamInjuries, awayTeamInjuries, onPlayerClick }) => {
    const [selectedTeam, setSelectedTeam] = useState('home'); // 'both', 'home', 'away'

    // Early return if no data
    if (!teamDepthCharts || teamDepthCharts.length === 0) {
        return (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                No lineup data available
            </div>
        );
    }

    // Find depth charts for each team
    const homeDepthChart = teamDepthCharts.find(dc => dc.TeamID === homeTeamID);
    const awayDepthChart = teamDepthCharts.find(dc => dc.TeamID === awayTeamID);

    // Helper function to get player stats
    const getPlayerStats = (teamID, playerID) => {
        if (!teamStats || !teamStats[teamID]) return null;
        return teamStats[teamID].find(stat => stat.PlayerID === playerID);
    };

    // Helper function to find player injury
    const getPlayerInjury = (teamID, playerID) => {
        const injuries = teamID === homeTeamID ? homeTeamInjuries : awayTeamInjuries;
        if (!injuries || injuries.length === 0) return null;
        return injuries.find(injury => injury.PlayerID === playerID);
    };

    // Helper function to get stat configuration for a position
    const getStatConfig = (position) => {
        // Map WR positions to WR stats
        if (['LWR', 'RWR', 'SWR'].includes(position)) return POSITION_STATS.WR;
        else if (["RB", "FB"].includes(position)) return POSITION_STATS.RB;
        return POSITION_STATS[position] || null;
    };

    // Group players by position
    const groupByPosition = (players) => {
        const grouped = {};
        players.forEach(player => {
            if (!grouped[player.Position]) {
                grouped[player.Position] = [];
            }
            grouped[player.Position].push(player);
        });
        // Sort players within each position by depth order
        Object.keys(grouped).forEach(pos => {
            grouped[pos].sort((a, b) => a.DepthOrder - b.DepthOrder);
        });
        return grouped;
    };

    // Render position group for a single team
    const renderPositionGroup = (position, players, teamID, teamObj, showStats = false) => {
        const statConfig = showStats ? getStatConfig(position) : null;
        const isClickable = STAT_POSITIONS.includes(position);

        return (
            <div key={position} className="mb-4">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    {position}
                </h4>
                <div className="space-y-1">
                    {players.map((player, idx) => {
                        const playerStat = showStats ? getPlayerStats(teamID, player.PlayerID) : null;
                        const injury = getPlayerInjury(teamID, player.PlayerID);
                        
                        return (
                            <div 
                                key={player.DepthChartID}
                                className={`px-3 py-2 rounded-md bg-gray-50 dark:bg-gray-700/50 ${
                                    isClickable ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors' : ''
                                }`}
                                onClick={isClickable ? () => onPlayerClick?.(player, teamID) : undefined}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-4">
                                            {player.DepthOrder}
                                        </span>
                                        <span className="text-sm text-gray-900 dark:text-gray-100">
                                            {player.Name}
                                        </span>
                                        {injury && (
                                            <span className={`hidden lg:inline text-[10px] px-1.5 py-0.5 rounded border font-medium ${getStatusStyling(injury.Status)}`}>
                                                {injury.Status}
                                            </span>
                                        )}
                                    </div>
                                    
                                    {statConfig && (
                                        <div className="flex gap-3 text-xs">
                                            {statConfig.map(stat => (
                                                <div key={stat.key} className="text-right">
                                                    <div className="text-gray-500 dark:text-gray-400">{stat.label}</div>
                                                    <div className="font-medium text-gray-900 dark:text-gray-100">
                                                        {playerStat ? stat.format(playerStat[stat.key]) : 'N/A'}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {injury && (
                                    <div className="lg:hidden mt-1 ml-6">
                                        <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded border font-medium ${getStatusStyling(injury.Status)}`}>
                                            {injury.Status}
                                        </span>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    // Render comparative position group (both teams side by side)
    const renderComparativePosition = (position, homeGrouped, awayGrouped, showStats = false) => {
        const homePlayers = homeGrouped[position] || [];
        const awayPlayers = awayGrouped[position] || [];
        
        if (homePlayers.length === 0 && awayPlayers.length === 0) return null;

        const statConfig = showStats ? getStatConfig(position) : null;
        const isClickable = STAT_POSITIONS.includes(position);

        return (
            <div key={position} className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 text-center">
                    {position}
                </h4>
                <div className="grid grid-cols-2 gap-4">
                    {/* Away Team */}
                    <div className="space-y-1">
                        {awayPlayers.map(player => {
                            const playerStat = showStats ? getPlayerStats(awayTeamID, player.PlayerID) : null;
                            const injury = getPlayerInjury(awayTeamID, player.PlayerID);
                            return (
                                <div 
                                    key={player.DepthChartID}
                                    className={`px-3 py-2 rounded-md bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 ${
                                        isClickable ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors' : ''
                                    }`}
                                    onClick={isClickable ? () => onPlayerClick?.(player, awayTeamID) : undefined}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2 flex-1 min-w-0">
                                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-4">
                                                {player.DepthOrder}
                                            </span>
                                            <span className="text-sm text-gray-900 dark:text-gray-100">
                                                {player.Name}
                                            </span>
                                            {injury && (
                                                <span className={`hidden lg:inline text-[10px] px-1.5 py-0.5 rounded border font-medium whitespace-nowrap ${getStatusStyling(injury.Status)}`}>
                                                    {injury.Status}
                                                </span>
                                            )}
                                        </div>
                                        {statConfig && (
                                            <div className="flex gap-2 text-xs">
                                                {statConfig.map(stat => (
                                                    <div key={stat.key} className="text-right min-w-[3rem]">
                                                        <div className="text-gray-500 dark:text-gray-400 text-[10px]">{stat.label}</div>
                                                        <div className="font-medium text-gray-900 dark:text-gray-100">
                                                            {playerStat ? stat.format(playerStat[stat.key]) : 'N/A'}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    {injury && (
                                        <div className="lg:hidden mt-1 ml-6">
                                            <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded border font-medium whitespace-nowrap ${getStatusStyling(injury.Status)}`}>
                                                {injury.Status}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Home Team */}
                    <div className="space-y-1">
                        {homePlayers.map(player => {
                            const playerStat = showStats ? getPlayerStats(homeTeamID, player.PlayerID) : null;
                            const injury = getPlayerInjury(homeTeamID, player.PlayerID);
                            return (
                                <div 
                                    key={player.DepthChartID}
                                    className={`px-3 py-2 rounded-md bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 ${
                                        isClickable ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors' : ''
                                    }`}
                                    onClick={isClickable ? () => onPlayerClick?.(player, homeTeamID) : undefined}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2 flex-1 min-w-0">
                                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-4">
                                                {player.DepthOrder}
                                            </span>
                                            <span className="text-sm text-gray-900 dark:text-gray-100">
                                                {player.Name}
                                            </span>
                                            {injury && (
                                                <span className={`hidden lg:inline text-[10px] px-1.5 py-0.5 rounded border font-medium whitespace-nowrap ${getStatusStyling(injury.Status)}`}>
                                                    {injury.Status}
                                                </span>
                                            )}
                                        </div>
                                        {statConfig && (
                                            <div className="flex gap-2 text-xs">
                                                {statConfig.map(stat => (
                                                    <div key={stat.key} className="text-right min-w-[3rem]">
                                                        <div className="text-gray-500 dark:text-gray-400 text-[10px]">{stat.label}</div>
                                                        <div className="font-medium text-gray-900 dark:text-gray-100">
                                                            {playerStat ? stat.format(playerStat[stat.key]) : 'N/A'}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    {injury && (
                                        <div className="lg:hidden mt-1 ml-6">
                                            <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded border font-medium whitespace-nowrap ${getStatusStyling(injury.Status)}`}>
                                                {injury.Status}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    };

    // Render section (Offense, Defense, or Special Teams)
    const renderSection = (title, positions, homeData, awayData) => {
        if (!homeData && !awayData) return null;

        const homeGrouped = homeData ? groupByPosition(homeData) : {};
        const awayGrouped = awayData ? groupByPosition(awayData) : {};

        return (
            <div className="mb-6">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b border-gray-200 dark:border-gray-600">
                    {title}
                </h3>
                
                {/* Desktop: Comparative Layout */}
                <div className="hidden lg:block">
                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="text-center">
                            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                {awayTeamLabel} (Away)
                            </span>
                        </div>
                        <div className="text-center">
                            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                {homeTeamLabel} (Home)
                            </span>
                        </div>
                    </div>
                    
                    {positions.map(pos => {
                        const showStats = STAT_POSITIONS.includes(pos);
                        return renderComparativePosition(pos, homeGrouped, awayGrouped, showStats);
                    })}
                </div>

                {/* Mobile: Toggle Layout */}
                <div className="block lg:hidden">
                    <div className="flex gap-2 mb-4">
                        <button
                            onClick={() => setSelectedTeam('away')}
                            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                                selectedTeam === 'away'
                                    ? 'bg-gray-700 dark:bg-gray-600 text-white'
                                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                            }`}
                        >
                            {awayTeamLabel}
                        </button>
                        <button
                            onClick={() => setSelectedTeam('home')}
                            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                                selectedTeam === 'home'
                                    ? 'bg-gray-700 dark:bg-gray-600 text-white'
                                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                            }`}
                        >
                            {homeTeamLabel}
                        </button>
                    </div>

                    {selectedTeam === 'away' && awayGrouped && (
                        <div>
                            {positions.map(pos => {
                                if (!awayGrouped[pos]) return null;
                                const showStats = STAT_POSITIONS.includes(pos);
                                return renderPositionGroup(pos, awayGrouped[pos], awayTeamID, awayTeamLabel, showStats);
                            })}
                        </div>
                    )}

                    {selectedTeam === 'home' && homeGrouped && (
                        <div>
                            {positions.map(pos => {
                                if (!homeGrouped[pos]) return null;
                                const showStats = STAT_POSITIONS.includes(pos);
                                return renderPositionGroup(pos, homeGrouped[pos], homeTeamID, homeTeamLabel, showStats);
                            })}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-center justify-between mb-4 relative mt-1">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Team Lineups</h3>
            </div>
            
            {/* Offense Section */}
            {renderSection(
                'Offense',
                OFFENSIVE_POSITIONS,
                homeDepthChart?.Offense,
                awayDepthChart?.Offense
            )}

            {/* Defense Section */}
            {renderSection(
                'Defense',
                DEFENSIVE_POSITIONS,
                homeDepthChart?.Defense,
                awayDepthChart?.Defense
            )}

            {/* Special Teams Section */}
            {(homeDepthChart?.SpecialTeams || awayDepthChart?.SpecialTeams) && (
                <div className="mb-6">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b border-gray-200 dark:border-gray-600">
                        Special Teams
                    </h3>
                    
                    {/* Desktop: Comparative Layout */}
                    <div className="hidden md:block">
                        <div className="grid grid-cols-2 gap-4">
                            {/* Away Team Special Teams */}
                            <div>
                                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 text-center">
                                    {awayTeamLabel}
                                </h4>
                                {awayDepthChart?.SpecialTeams && (
                                    <div className="space-y-1">
                                        {groupByPosition(awayDepthChart.SpecialTeams) && 
                                            Object.entries(groupByPosition(awayDepthChart.SpecialTeams)).map(([pos, players]) => (
                                                <div key={pos} className="mb-3">
                                                    <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">{pos}</div>
                                                    {players.map(player => {
                                                        const injury = getPlayerInjury(awayTeamID, player.PlayerID);
                                                        return (
                                                            <div 
                                                                key={player.DepthChartID}
                                                                className="px-3 py-2 rounded-md bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600"
                                                            >
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-4">
                                                                        {player.DepthOrder}
                                                                    </span>
                                                                    <span className="text-sm text-gray-900 dark:text-gray-100">
                                                                        {player.Name}
                                                                    </span>
                                                                    {injury && (
                                                                        <span className={`hidden lg:inline text-[10px] px-1.5 py-0.5 rounded border font-medium whitespace-nowrap ${getStatusStyling(injury.Status)}`}>
                                                                            {injury.Status}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                {injury && (
                                                                    <div className="lg:hidden mt-1 ml-6">
                                                                        <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded border font-medium whitespace-nowrap ${getStatusStyling(injury.Status)}`}>
                                                                            {injury.Status}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ))
                                        }
                                    </div>
                                )}
                            </div>

                            {/* Home Team Special Teams */}
                            <div>
                                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 text-center">
                                    {homeTeamLabel}
                                </h4>
                                {homeDepthChart?.SpecialTeams && (
                                    <div className="space-y-1">
                                        {groupByPosition(homeDepthChart.SpecialTeams) && 
                                            Object.entries(groupByPosition(homeDepthChart.SpecialTeams)).map(([pos, players]) => (
                                                <div key={pos} className="mb-3">
                                                    <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">{pos}</div>
                                                    {players.map(player => {
                                                        const injury = getPlayerInjury(homeTeamID, player.PlayerID);
                                                        return (
                                                            <div 
                                                                key={player.DepthChartID}
                                                                className="px-3 py-2 rounded-md bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600"
                                                            >
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-4">
                                                                        {player.DepthOrder}
                                                                    </span>
                                                                    <span className="text-sm text-gray-900 dark:text-gray-100">
                                                                        {player.Name}
                                                                    </span>
                                                                    {injury && (
                                                                        <span className={`hidden lg:inline text-[10px] px-1.5 py-0.5 rounded border font-medium whitespace-nowrap ${getStatusStyling(injury.Status)}`}>
                                                                            {injury.Status}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                {injury && (
                                                                    <div className="lg:hidden mt-1 ml-6">
                                                                        <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded border font-medium whitespace-nowrap ${getStatusStyling(injury.Status)}`}>
                                                                            {injury.Status}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ))
                                        }
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Mobile: Toggle Layout */}
                    <div className="block md:hidden">
                        {selectedTeam === 'away' && awayDepthChart?.SpecialTeams && (
                            <div>
                                {Object.entries(groupByPosition(awayDepthChart.SpecialTeams)).map(([pos, players]) => (
                                    <div key={pos} className="mb-4">
                                        <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{pos}</div>
                                        <div className="space-y-1">
                                            {players.map(player => {
                                                const injury = getPlayerInjury(awayTeamID, player.PlayerID);
                                                return (
                                                    <div 
                                                        key={player.DepthChartID}
                                                        className="px-3 py-2 rounded-md bg-gray-50 dark:bg-gray-700/50"
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-4">
                                                                {player.DepthOrder}
                                                            </span>
                                                            <span className="text-sm text-gray-900 dark:text-gray-100">
                                                                {player.Name}
                                                            </span>
                                                            {injury && (
                                                                <span className={`hidden lg:inline text-[10px] px-1.5 py-0.5 rounded border font-medium whitespace-nowrap ${getStatusStyling(injury.Status)}`}>
                                                                    {injury.Status}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {injury && (
                                                            <div className="lg:hidden mt-1 ml-6">
                                                                <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded border font-medium whitespace-nowrap ${getStatusStyling(injury.Status)}`}>
                                                                    {injury.Status}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {selectedTeam === 'home' && homeDepthChart?.SpecialTeams && (
                            <div>
                                {Object.entries(groupByPosition(homeDepthChart.SpecialTeams)).map(([pos, players]) => (
                                    <div key={pos} className="mb-4">
                                        <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{pos}</div>
                                        <div className="space-y-1">
                                            {players.map(player => {
                                                const injury = getPlayerInjury(homeTeamID, player.PlayerID);
                                                return (
                                                    <div 
                                                        key={player.DepthChartID}
                                                        className="px-3 py-2 rounded-md bg-gray-50 dark:bg-gray-700/50"
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-4">
                                                                {player.DepthOrder}
                                                            </span>
                                                            <span className="text-sm text-gray-900 dark:text-gray-100">
                                                                {player.Name}
                                                            </span>
                                                            {injury && (
                                                                <span className={`hidden lg:inline text-[10px] px-1.5 py-0.5 rounded border font-medium whitespace-nowrap ${getStatusStyling(injury.Status)}`}>
                                                                    {injury.Status}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {injury && (
                                                            <div className="lg:hidden mt-1 ml-6">
                                                                <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded border font-medium whitespace-nowrap ${getStatusStyling(injury.Status)}`}>
                                                                    {injury.Status}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NflTeamLineups;